import { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  NumericInput,
} from '@blueprintjs/core';
import { loan_application } from '@axo/shared/data-access/types';
import { formatCurrency } from '../../utils/formatCurrency';
import { CreditCardAmountLimits } from './constants';

interface CreditLimitDialogProps {
  isOpen: boolean;
  limits: CreditCardAmountLimits;
  currency: loan_application.Currency | null | undefined;
  onConfirm: (appliedAmount: number) => void;
  onCancel: () => void;
}

export const CreditLimitDialog: React.FC<CreditLimitDialogProps> = ({
  isOpen,
  limits,
  currency,
  onConfirm,
  onCancel,
}) => {
  const [amount, setAmount] = useState(limits.default);

  const handleOpening = useCallback(() => {
    setAmount(limits.default);
  }, [limits.default]);

  const isValid = !isNaN(amount) && amount > 0;

  const handleConfirm = useCallback(() => {
    onConfirm(isValid ? amount : limits.default);
  }, [onConfirm, isValid, amount, limits.default]);

  const handleValueChange = useCallback((val: number) => {
    setAmount(val);
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      onOpening={handleOpening}
      icon="credit-card"
      title="Set credit limit"
    >
      <DialogBody>
        <FormGroup
          label="Credit limit"
          helperText={`Enter the desired credit limit for this application. Default is ${formatCurrency(
            limits.default,
            currency
          )}.`}
        >
          <NumericInput
            value={amount}
            onValueChange={handleValueChange}
            min={limits.min}
            max={limits.max}
            stepSize={limits.step}
            fill
            allowNumericCharactersOnly
            buttonPosition="none"
            placeholder="Enter amount"
          />
        </FormGroup>
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              intent="primary"
              onClick={handleConfirm}
              disabled={!isValid}
            >
              Add application
            </Button>
          </>
        }
      />
    </Dialog>
  );
};
